import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import Tooltip from 'ember-tooltips/components/ember-tooltip';
import { and } from 'ember-truth-helpers';
import MultiCheckbox from 'garaje/components/multi-checkbox';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import { FlowType } from 'garaje/utils/enums';
import _sortBy from 'lodash/sortBy';
import { cached } from 'tracked-toolbox';

type AnyFlow = Flow | GlobalFlow;

interface MultiFlowSelectComponentSignature {
  Args: {
    flows: AnyFlow[];
    /**
     * Set the `@renderInPlace` attribute of the BasicDropdown.
     * Defaults to `true` if not provided.
     */
    renderInPlace?: boolean;
    selectedFlows: AnyFlow[];
    triggerClass?: string;

    selectedFlowsChanged: (flows: AnyFlow[]) => unknown;
  };
}

const VISITOR_FLOW_TYPES = [FlowType.VISITOR_SCREENING, FlowType.GLOBAL_CHILD, FlowType.PROPERTY_WALKUP];

const DEFAULT_FLOW_TYPE = FlowType.VISITOR_SCREENING;

// Flow#type comes back from the real API as `null` for Visitor flows but the FlowType enum uses '' for VISITOR_SCREENING
function flowType(flow: AnyFlow): FlowType {
  if (flow.isGlobal) {
    return DEFAULT_FLOW_TYPE;
  }
  return flow.type ?? DEFAULT_FLOW_TYPE;
}

export default class MultiFlowSelectComponent extends Component<MultiFlowSelectComponentSignature> {
  get allVisitorFlowsCheckboxId(): string {
    return `${guidFor(this)}-all-visitor-flows`;
  }

  @cached
  get employeeFlows(): AnyFlow[] {
    return this.args.flows.filter((flow) => flowType(flow) === FlowType.EMPLOYEE_SCREENING) ?? [];
  }

  get hasAllEmployeeFlowsSelected(): boolean {
    return (
      this.selectedEmployeeFlows.length > 0 &&
      this.employeeFlows.every((flow) => this.selectedEmployeeFlows.includes(flow))
    );
  }

  @cached
  get hasAllVisitorFlowsSelected(): boolean {
    return (
      this.selectedVisitorFlows.length > 0 &&
      this.visitorFlows.every((flow) => this.selectedVisitorFlows.includes(flow))
    );
  }

  get hasNoEmployeeFlowsSelected(): boolean {
    return this.selectedEmployeeFlows.length === 0;
  }

  get hasNoVisitorFlowsSelected(): boolean {
    return this.selectedVisitorFlows.length === 0;
  }

  get hasSomeEmployeeFlowsSelected(): boolean {
    return this.selectedEmployeeFlows.length > 0;
  }

  get hasSomeVisitorFlowsSelected(): boolean {
    return this.selectedVisitorFlows.length > 0 && this.selectedVisitorFlows.length !== this.visitorFlows.length;
  }

  get labelText(): string {
    if (this.args.selectedFlows.length === 1) {
      // when only one flow is selected, use its name as the label
      return this.args.selectedFlows[0]!.name;
    } else if (this.hasAllVisitorFlowsSelected && this.hasNoEmployeeFlowsSelected) {
      return 'All Visitor types';
    } else if (this.hasAllEmployeeFlowsSelected && this.hasNoVisitorFlowsSelected) {
      return 'All Employee types';
    }
    return 'Multiple types';
  }

  get renderInPlace(): boolean {
    return this.args.renderInPlace ?? true;
  }

  @cached
  get selectedEmployeeFlows(): AnyFlow[] {
    return this.args.selectedFlows.filter((flow) => flowType(flow) === FlowType.EMPLOYEE_SCREENING);
  }

  @cached
  get selectedVisitorFlows(): AnyFlow[] {
    return this.args.selectedFlows.filter((flow) => VISITOR_FLOW_TYPES.includes(flowType(flow)));
  }

  get tooltipText(): string | null {
    if (this.args.selectedFlows.length === 0) return null;
    return this.args.selectedFlows
      .map((flow) => flow.name)
      .sort()
      .join(', ');
  }

  @cached
  get visitorFlows(): AnyFlow[] {
    return this.args.flows.filter((flow) => VISITOR_FLOW_TYPES.includes(flowType(flow)));
  }

  @action
  selectOrDeselectAllVisitorFlows(): void {
    if (this.hasAllVisitorFlowsSelected) {
      // deselect all
      this.updateVisitorFlowSelection([]);
    } else {
      // select all
      this.updateVisitorFlowSelection(this.visitorFlows);
    }
  }

  @action
  updateEmployeeFlowSelection(selectedFlows: AnyFlow[]): void {
    const allSelectedFlows = [...this.selectedVisitorFlows, ...selectedFlows];

    this.args.selectedFlowsChanged(allSelectedFlows);
  }

  @action
  updateVisitorFlowSelection(selectedFlows: AnyFlow[]): void {
    const allSelectedFlows = _sortBy([...this.selectedEmployeeFlows, ...selectedFlows], 'id');

    this.args.selectedFlowsChanged(allSelectedFlows);
  }

  <template>
    <BasicDropdown
      @matchTriggerWidth={{false}}
      @renderInPlace={{this.renderInPlace}}
      @verticalPosition="below"
      as |dropdown|
    >
      <dropdown.Trigger class="ember-power-select-trigger {{@triggerClass}}" data-test-multi-flow-select-trigger>
        {{#if this.tooltipText}}
          <Tooltip
            @side="top"
            @tooltipClass="!rounded-none !p-2 !border !border-carbon-20 !text-carbon-80 !bg-white text-shadow-none z-50"
          >
            {{this.tooltipText}}
          </Tooltip>
        {{/if}}
        {{this.labelText}}
      </dropdown.Trigger>
      <dropdown.Content>
        <div class="ember-power-select-dropdown py-2 w-max max-h-[50vh] overflow-auto" data-test-flows>
          {{#if this.visitorFlows}}
            <div class="px-3" data-test-visitor-flows>
              <label class="!text-carbon-70 !font-bold flex items-center space-x-1">
                <input
                  type="checkbox"
                  checked={{this.hasAllVisitorFlowsSelected}}
                  {{! @glint-expect-error - Not an attribute that can be set directly. Check if this works }}
                  indeterminate={{this.hasSomeVisitorFlowsSelected}}
                  data-test-all-flows
                  {{on "change" this.selectOrDeselectAllVisitorFlows}}
                />
                <p class="text-base mb-0">Visitor types</p>
              </label>
              <div class="ember-power-select-options !max-h-full" data-test-visitor-flows-list>
                <MultiCheckbox
                  @items={{this.visitorFlows}}
                  @selectedDidChange={{this.updateVisitorFlowSelection}}
                  @selectedItems={{this.selectedVisitorFlows}}
                  as |flow checkbox isSelected|
                >
                  <label
                    class="ml-3 flex items-center space-x-1 ember-power-select-option
                      {{if isSelected '!text-gem-60 selected' '!text-carbon-70'}}"
                    data-test-flow={{flow.id}}
                  >
                    <checkbox.checkbox />
                    <p class="text-base mb-[-2px]">{{flow.name}}</p>
                  </label>
                </MultiCheckbox>
              </div>
            </div>
          {{/if}}

          {{#if (and this.visitorFlows this.employeeFlows)}}
            {{! only show separator line when we're showing both sections }}
            <hr class="mt-2 mb-4" />
          {{/if}}

          {{#if this.employeeFlows}}
            <div class="ember-power-select-options !max-h-full px-3" data-test-employee-flows>
              <p class="text-base font-bold mb-0">Employee registration</p>
              <MultiCheckbox
                @items={{this.employeeFlows}}
                @selectedDidChange={{this.updateEmployeeFlowSelection}}
                @selectedItems={{this.selectedEmployeeFlows}}
                as |flow checkbox isSelected|
              >
                <label
                  class="ml-3 flex items-center space-x-1 ember-power-select-option
                    {{if isSelected '!text-gem-60 selected' '!text-carbon-70'}}"
                  data-test-flow={{flow.id}}
                >
                  <checkbox.checkbox />
                  <p class="text-base mb-0">{{flow.name}}</p>
                </label>
              </MultiCheckbox>
            </div>
          {{/if}}
        </div>
      </dropdown.Content>
    </BasicDropdown>
  </template>
}
